import { addHours, addMinutes, subHours, subMinutes } from "date-fns";

// Temperature conversion functions
export const fahrenheitToCelsius = (f) => (f - 32) * (5 / 9);
export const celsiusToFahrenheit = (c) => (c * 9) / 5 + 32;

// Calculate bulk fermentation time based on temperature
const calculateBulkFermentationTime = (temperatureF) => {
  // Base time is 4 hours at 75°F (24°C)
  const baseTemp = 75;
  const baseTime = 4;

  if (!temperatureF) {
    return { hours: baseTime, max: 6 };
  }

  // Convert temperature difference to Celsius for calculation
  // Every 2°F change = ~30 minutes
  const tempDiff = (baseTemp - temperatureF) / 2;
  const adjustedTime = baseTime + tempDiff * 0.5;

  // Ensure time stays within reasonable bounds
  const hours = Math.max(2.5, Math.min(7, adjustedTime));
  const max = Math.min(8, hours + 2);

  return {
    hours: Number(hours.toFixed(1)),
    max,
    temperature: temperatureF,
  };
};

const STEPS = [
  {
    name: "Feed Starter",
    duration: { hours: 6 },
    type: "fixed",
  },
  {
    name: "Mix Dough",
    duration: { minutes: 30 },
    type: "fixed",
  },
  {
    name: "Autolyse (Rest Dough)",
    duration: { hours: 1 },
    type: "fixed",
  },
  {
    name: "Add Salt & Stretch-Folds",
    duration: { hours: 2, max: 3 },
    type: "flexible",
  },
  {
    name: "Bulk Fermentation",
    duration: { hours: 4, max: 6 },
    type: "flexible",
    temperatureDependent: true,
  },
  {
    name: "Pre-Shape Dough",
    duration: { minutes: 15 },
    type: "fixed",
  },
  {
    name: "Bench Rest",
    duration: { minutes: 30 },
    type: "fixed",
  },
  {
    name: "Final Shaping",
    duration: { minutes: 15 },
    type: "fixed",
  },
  {
    name: "Cold Proof (Refrigeration)",
    duration: { hours: 12, max: 24 },
    type: "flexible",
  },
  {
    name: "Preheat Oven",
    duration: { hours: 1 },
    type: "fixed",
  },
  {
    name: "Bake Bread",
    duration: { minutes: 45 },
    type: "fixed",
  },
  {
    name: "Rest & Cool",
    duration: { hours: 1 },
    type: "fixed",
  },
];

export const calculateTimeline = (
  startTime,
  temperature,
  customDurations = {},
  direction = "forward"
) => {
  let currentTime = new Date(startTime);

  const timeline = STEPS.map((step) => {
    const start = new Date(currentTime);
    let duration = customDurations[step.name] || step.duration;

    // Adjust bulk fermentation time based on temperature
    if (step.temperatureDependent && temperature) {
      duration = calculateBulkFermentationTime(temperature);
    }

    // Calculate end time
    if (direction === "forward") {
      if (duration.hours) {
        currentTime = addHours(currentTime, duration.hours);
      }
      if (duration.minutes) {
        currentTime = addMinutes(currentTime, duration.minutes);
      }
    } else {
      if (duration.hours) {
        currentTime = subHours(currentTime, duration.hours);
      }
      if (duration.minutes) {
        currentTime = subMinutes(currentTime, duration.minutes);
      }
    }

    const end = new Date(currentTime);
    return {
      ...step,
      startTime: direction === "forward" ? start : end,
      endTime: direction === "forward" ? end : start,
      duration,
    };
  });

  return direction === "forward" ? timeline : timeline.reverse();
};

export const getSteps = () => STEPS;
